import React, { useRef, useEffect, MouseEvent, useCallback, useMemo } from "react";
import { useSelectionActions, useIsItemSelected } from "@contexts/SelectedItemsProvider";
import { useShiftKey } from "@helpers/selection";
import { handleWithModifiers, useKeyboard } from "@helpers/useKeyboard";
import { Asset, Checkbox } from "@thenounproject/lingo-core";
import { handleArrowSelection } from "@features/library/handleArrowSelection";

export function useAssetSelection(data: { assets?: Asset[] }) {
  const { setSelectedItems, addItems, deselectItems, clearSelection, isSelected, getSelection } =
    useSelectionActions();

  const assetsRef = useRef<Asset[]>(data?.assets || []);
  const cachedSelectedItems = useRef<string[]>(null);
  const checkboxAnchor = useRef<string>(null);
  const shiftDown = useShiftKey();
  const shiftDownRef = useRef(shiftDown);

  useEffect(() => {
    assetsRef.current = data?.assets || [];
  }, [data?.assets]);

  useEffect(() => {
    shiftDownRef.current = shiftDown;
  }, [shiftDown]);

  useEffect(() => {
    if (!shiftDown) {
      checkboxAnchor.current = null;
      cachedSelectedItems.current = null;
    }
  }, [shiftDown]);

  // Row click handler (shift/meta/default)
  const selectAsset = useCallback(
    (asset: Asset, event: MouseEvent) => {
      const currentSelection = getSelection("assetLibrary");
      const allAssets = assetsRef.current;
      let selectedBeforeClick = currentSelection;
      document.getSelection().removeAllRanges();

      handleWithModifiers(event, {
        shiftAction: () => {
          if (currentSelection.length > 0) {
            if (!cachedSelectedItems.current || cachedSelectedItems.current.length === 0) {
              cachedSelectedItems.current = currentSelection;
            }
            selectedBeforeClick = cachedSelectedItems.current;
            const lastSelectedAssetId = selectedBeforeClick[selectedBeforeClick.length - 1];
            const anchorIndex = allAssets.findIndex(a => a.id === lastSelectedAssetId);
            const clickedIndex = allAssets.findIndex(a => a.id === asset.id);
            const [start, end] = [anchorIndex, clickedIndex].sort((a, b) => a - b);
            const itemsInRange = allAssets.slice(start, end + 1).map(a => a.id);

            setSelectedItems({ type: "assetLibrary", ids: itemsInRange });
          }
        },
        metaAction: () => {
          if (isSelected("assetLibrary", asset?.id)) {
            deselectItems({ type: "assetLibrary", ids: [asset?.id] });
          } else {
            addItems({ type: "assetLibrary", ids: [asset?.id] });
          }
        },
        default: () => {
          clearSelection({ type: "assetLibrary" });
          setSelectedItems({ type: "assetLibrary", ids: [asset.id] });
        },
      });
    },
    [setSelectedItems, addItems, deselectItems, clearSelection, isSelected, getSelection]
  );

  // Checkbox click handler (toggle / shift-range)
  const onCheckboxClick = useCallback(
    (asset: Asset) => {
      const currentSelection = getSelection("assetLibrary");
      const allAssets = assetsRef.current;

      if (shiftDownRef.current && currentSelection.length > 0) {
        const anchorId = checkboxAnchor.current;
        if (!anchorId) {
          checkboxAnchor.current = currentSelection[0];
        }
        if (currentSelection.includes(asset.id)) {
          deselectItems({ type: "assetLibrary", ids: [asset.id] });
        } else {
          const anchorIndex = allAssets.findIndex(a => a.id === checkboxAnchor.current);
          const clickedIndex = allAssets.findIndex(a => a.id === asset.id);
          const [start, end] = [anchorIndex, clickedIndex].sort((a, b) => a - b);
          const itemsInRange = allAssets.slice(start, end + 1).map(a => a.id);
          const newSelection = [...new Set([...currentSelection, ...itemsInRange])];
          clearSelection({ type: "assetLibrary" });
          setSelectedItems({ type: "assetLibrary", ids: newSelection });
        }
      } else {
        if (currentSelection.includes(asset?.id)) {
          deselectItems({ type: "assetLibrary", ids: [asset?.id] });
          checkboxAnchor.current = null;
        } else {
          addItems({ type: "assetLibrary", ids: [asset?.id] });
          checkboxAnchor.current = asset.id;
        }
      }
    },
    [getSelection, setSelectedItems, addItems, deselectItems, clearSelection]
  );

  // Stable ref for the checkbox cell to access
  const onCheckboxClickRef = useRef(onCheckboxClick);
  onCheckboxClickRef.current = onCheckboxClick;

  // CheckboxCell component — stable identity, never recreated.
  // Each instance subscribes to only its own asset's selection via useIsItemSelected.
  const CheckboxCell = useMemo(() => {
    const Cell: React.FC<{ data: Asset; rowIndex: number }> = ({ data: asset }) => {
      const selected = useIsItemSelected("assetLibrary", asset?.id);
      const onClick = useCallback(() => onCheckboxClickRef.current(asset), [asset]);
      return React.createElement(Checkbox, { isSelected: selected, onClick });
    };
    Cell.displayName = "AssetSelectionCheckbox";
    return React.memo(Cell);
  }, []);

  // Arrow key handler
  const handleArrowKey = useCallback(
    (e: KeyboardEvent) => {
      const currentSelection = getSelection("assetLibrary");
      handleArrowSelection(e, currentSelection, assetsRef.current, setSelectedItems, deselectItems);
    },
    [getSelection, setSelectedItems, deselectItems]
  );

  useKeyboard({ onArrowKey: handleArrowKey });

  return {
    isSelected: (asset: Asset) => isSelected("assetLibrary", asset?.id),
    onClick: onCheckboxClick,
    selectAsset,
    disabled: () => false,
    CheckboxCell,
  };
}
